import { getScopedDomain } from '../lib/tenantScope';
// Reports landing — card-tile layout with 7 categories. Each tile links to a
// concrete report route. Toolbar mirrors 3CX (Purge / Call Types / Call Costs /
// My reports). Detail views fetch from /api/reports/<endpoint> directly so
// every tile renders a working table.

import { useMemo, useState } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Trash2, Phone, DollarSign, FileBarChart2,
  Brain, ScrollText, Server, Users2, Activity,
  ChevronRight, ArrowLeft, Download,
} from 'lucide-react';
import { getToken } from '../lib/api';
import CallLegs from './CallLegs';

const auth = () => ({ Authorization: `Bearer ${getToken()}` });

type Tile = {
  id: string;            // url segment under /reports/
  label: string;
  endpoint: string;      // path under /api/reports/
  withDates?: boolean;   // adds ?from&to query params
  hideDates?: boolean;   // top-level dimensions list — no date filter
};
type Category = { title: string; icon: any; color: string; tiles: Tile[] };

// Endpoint mapping per tile — every tile has a working backing route under
// /api/reports/. Endpoints come from api/routes/reports.js + reports_extended.js.
const LEFT_COLUMN: Category[] = [
  {
    title: 'AI Summary and Sentiment',
    icon: Brain,
    color: 'text-violet-400',
    tiles: [
      { id: 'call-log',          label: 'Call Log',                              endpoint: 'call-log',           withDates: true },
      { id: 'inbound-calls',     label: 'Inbound Calls',                         endpoint: 'inbound-calls',      withDates: true },
      { id: 'outbound-calls',    label: 'Outbound Calls',                        endpoint: 'outbound-calls',     withDates: true },
      { id: 'extension-stats',   label: 'Extension Statistics',                  endpoint: 'extensions',         withDates: true },
      { id: 'ring-groups',       label: 'Ring Groups',                           endpoint: 'ring-groups',        hideDates: true },
      { id: 'queue-answered-wait', label: 'Queue Answered Calls By Waiting Time', endpoint: 'queue-answered-wait', withDates: true },
    ],
  },
  {
    title: 'Logs',
    icon: ScrollText,
    color: 'text-sky-400',
    tiles: [
      { id: 'chat-log',          label: 'Chat Log',  endpoint: 'chat-log',  withDates: true },
      { id: 'audit-log',         label: 'Audit Log', endpoint: 'audit-log', withDates: true },
    ],
  },
  {
    title: 'System',
    icon: Server,
    color: 'text-emerald-400',
    tiles: [
      { id: 'inbound-rules',     label: 'Inbound Rules', endpoint: 'inbound-rules', hideDates: true },
    ],
  },
  {
    title: 'Queue Statistics',
    icon: FileBarChart2,
    color: 'text-amber-400',
    tiles: [
      { id: 'queue-abandoned',        label: 'Abandoned Queue Calls',         endpoint: 'queue-abandoned',         withDates: true },
      { id: 'queue-callbacks',        label: 'Queue Callbacks',               endpoint: 'queue-callbacks',         withDates: true },
      { id: 'queue-failed-callbacks', label: 'Queue Failed Callbacks',        endpoint: 'queue-failed-callbacks',  withDates: true },
      { id: 'sla-statistics',         label: 'SLA Statistics',                endpoint: 'queue-sla',               withDates: true },
      { id: 'sla-breaches',           label: 'SLA Breaches',                  endpoint: 'sla-breaches',            withDates: true },
      { id: 'queue-performance',      label: 'Queue Performance Overview',    endpoint: 'queue-performance',       withDates: true },
      { id: 'queue-detailed',         label: 'Detailed Queue Statistics',     endpoint: 'queue-detailed',          withDates: true },
      { id: 'queue-team',             label: 'Team Queue General Statistics', endpoint: 'queue-team',              withDates: true },
    ],
  },
];

const RIGHT_COLUMN: Category[] = [
  {
    title: 'Agent Statistics',
    icon: Users2,
    color: 'text-rose-400',
    tiles: [
      { id: 'agent-in-queue',       label: 'Agent in Queue Statistics', endpoint: 'agent-in-queue',       withDates: true },
      { id: 'agent-login-history',  label: 'Agent Login History',       endpoint: 'agent-login-history',  withDates: true },
    ],
  },
  {
    title: 'Extension Statistics',
    icon: Activity,
    color: 'text-cyan-400',
    tiles: [
      { id: 'user-activity',                 label: 'User Activity',               endpoint: 'user-activity',                 withDates: true },
      { id: 'call-distribution',             label: 'Call Distribution',           endpoint: 'call-distribution',             withDates: true },
      { id: 'cost-by-extension-department',  label: 'Call Cost By Extension Dept', endpoint: 'cost-by-extension-department',  withDates: true },
    ],
  },
  {
    title: 'Chat Statistics',
    icon: FileBarChart2,
    color: 'text-pink-400',
    tiles: [
      { id: 'queue-chat-performance',  label: 'Queue Chat Performance', endpoint: 'queue-chat-performance', withDates: true },
      { id: 'queue-agents-chat',       label: 'Queue Agents Chat',      endpoint: 'chat-agents',           withDates: true },
      { id: 'abandoned-chats',         label: 'Abandoned Chats',        endpoint: 'chat-abandoned',         withDates: true },
    ],
  },
];

const CATEGORIES: Category[] = [...LEFT_COLUMN, ...RIGHT_COLUMN];
const TILES_BY_ID: Record<string, Tile> = Object.fromEntries(
  CATEGORIES.flatMap(c => c.tiles.map(t => [t.id, t]))
);

export default function Reports() {
  return (
    <Routes>
      <Route index element={<ReportsLanding />} />
      <Route path="call-history" element={<CallLegs />} />
      <Route path=":reportId" element={<ReportDetail />} />
    </Routes>
  );
}

// ─────────────────────────────────────── Landing ──────────────────────────────

function ReportsLanding() {
  const navigate = useNavigate();
  const [purgeOpen, setPurgeOpen] = useState(false);

  return (
    <div className="space-y-3">
      <h1 className="text-2xl font-semibold">Reports</h1>

      {/* Toolbar — 3CX-shape: Purge / Call Types / Call Costs / My reports */}
      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => setPurgeOpen(true)}>
          <Trash2 size={14} /> Purge
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => navigate('/reports/call-types')}>
          <Phone size={14} /> Call Types
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => navigate('/reports/call-costs')}>
          <DollarSign size={14} /> Call Costs
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => navigate('/reports/my-reports')}>
          <FileBarChart2 size={14} /> My reports
        </button>
      </div>

      {/* Two-column masonry — left col is wider so the long Queue Statistics
          card fits without wrapping; right col has the 3 narrower cards. */}
      <div className="grid gap-3 grid-cols-1 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2">
          {LEFT_COLUMN.map(cat => <CategoryCard key={cat.title} cat={cat} />)}
        </div>
        <div className="space-y-3">
          {RIGHT_COLUMN.map(cat => <CategoryCard key={cat.title} cat={cat} />)}
        </div>
      </div>

      {purgeOpen && <PurgeModal onClose={() => setPurgeOpen(false)} />}
    </div>
  );
}

function CategoryCard({ cat }: { cat: Category }) {
  const Icon = cat.icon;
  return (
    <div className="card p-3">
      <div className="flex items-center gap-2 pb-2 mb-2 border-b border-border">
        <Icon size={18} className={cat.color} />
        <div className="font-medium text-sm">{cat.title}</div>
      </div>
      <ul className="space-y-1">
        {cat.tiles.map(t => (
          <li key={t.id}>
            <Link to={`/reports/${t.id}`} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-elevated">
              <ChevronRight size={12} className="text-textSecondary shrink-0" />
              <span className="flex-1">{t.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────── Detail ───────────────────────────────

function ReportDetail() {
  const { reportId = '' } = useParams();
  const tile = TILES_BY_ID[reportId];

  const today    = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to,   setTo]   = useState(today);

  const url = useMemo(() => {
    if (!tile) return null;
    const p = new URLSearchParams();
    if (tile.withDates) { p.set('from', from); p.set('to', to); }
    const d = getScopedDomain();           // department filter (if one is selected)
    if (d) p.set('domain_uuid', d);
    const qs = p.toString();
    return `/api/reports/${tile.endpoint}${qs ? '?' + qs : ''}`;
  }, [tile, from, to]);

  const q = useQuery<any>({
    queryKey: ['report', reportId, from, to],
    queryFn: async () => {
      if (!url) return [];
      const r = await fetch(url, { headers: auth() });
      if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
      return r.json();
    },
    enabled: !!url,
  });

  // Tolerate {data,note} | {rows} | array shapes from different endpoints.
  const { rows, note } = useMemo(() => {
    const d: any = q.data;
    if (Array.isArray(d))                   return { rows: d as any[], note: '' };
    if (d && Array.isArray(d.rows))         return { rows: d.rows as any[], note: d.note || '' };
    if (d && Array.isArray(d.data))         return { rows: d.data as any[], note: d.note || '' };
    if (d && Array.isArray(d.queues))       return { rows: d.queues as any[], note: d.note || '' };
    if (d && typeof d === 'object')         return { rows: [d], note: '' };
    return { rows: [] as any[], note: '' };
  }, [q.data]);

  const cols = rows[0] ? Object.keys(rows[0]) : [];
  const label = tile?.label || reportId;

  function downloadCsv() {
    if (!rows.length) return;
    const head = cols.join(',');
    const body = rows.map(r => cols.map(c => csvCell(r[c])).join(',')).join('\n');
    const blob = new Blob([head + '\n' + body], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${reportId}-${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Link to="/reports" className="btn-ghost p-1" title="Back"><ArrowLeft size={16} /></Link>
        <h1 className="text-2xl font-semibold flex-1">{label}</h1>
        <button className="btn-ghost text-sm inline-flex items-center gap-1"
                disabled={!rows.length} onClick={downloadCsv} title="Download CSV">
          <Download size={14} /> Export
        </button>
      </div>

      {tile?.withDates && !tile?.hideDates && (
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="block text-sm text-textSecondary mb-1">From</label>
            <input className="input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">To</label>
            <input className="input" type="date" value={to} onChange={e => setTo(e.target.value)} />
          </div>
        </div>
      )}

      {note && (
        <div className="card p-2 text-sm text-textSecondary">{note}</div>
      )}

      <div className="card p-0 overflow-auto max-h-[70vh]">
        {q.isLoading ? (
          <div className="p-6 text-center text-textSecondary text-sm">Loading…</div>
        ) : q.isError ? (
          <div className="p-6 text-center text-danger text-sm">Error: {String((q.error as any)?.message || q.error)}</div>
        ) : !rows.length ? (
          <div className="p-6 text-center text-textSecondary text-sm">No data.</div>
        ) : (
          <table className="table">
            <thead><tr>{cols.map(c => <th key={c}>{prettyHeader(c)}</th>)}</tr></thead>
            <tbody>
              {rows.map((r: any, i: number) => (
                <tr key={i} className="hover:bg-elevated">
                  {cols.map(c => <td key={c} className="text-xs">{format(r[c])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function format(v: any): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object')          return JSON.stringify(v);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try { return new Date(v).toLocaleString(); } catch { return v; }
  }
  return String(v);
}

function csvCell(v: any): string {
  const s = v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function prettyHeader(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ─────────────────────────────────────── Purge modal ──────────────────────────

function PurgeModal({ onClose }: { onClose: () => void }) {
  const [from, setFrom] = useState('');
  const [to,   setTo]   = useState('');
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState('');

  async function go() {
    if (!from || !to) { setErr('Pick both dates'); return; }
    if (!confirm(`Permanently delete call records from ${from} to ${to}? This cannot be undone.`)) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`/api/reports/purge?from=${from}&to=${to}`, { method: 'POST', headers: auth() });
      if (!r.ok) throw new Error(await r.text());
      onClose();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="card p-4 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">Purge call data</h2>
        <p className="text-sm text-textSecondary mb-3">
          Permanently delete call records in the selected range. This cannot be undone.
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="block text-xs text-textSecondary mb-1">From</label>
            <input className="input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-textSecondary mb-1">To</label>
            <input className="input" type="date" value={to} onChange={e => setTo(e.target.value)} />
          </div>
        </div>
        {err && <div className="text-sm text-danger mb-2">{err}</div>}
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-danger" disabled={busy} onClick={go}>{busy ? 'Purging…' : 'Purge'}</button>
        </div>
      </div>
    </div>
  );
}
